import {createSelector} from 'reselect';

import {isDefined} from '@stubhub/general-utils';

import {BARCODE} from '../../apps/sellTicket/config';
import {shouldExcludeEUBusinessSeller} from '../../apps/sellTicket/utils/sellerFrameworkUtils';
import {getDisclosureMsgType, shouldShowDisclosure} from '../../apps/sellTicket/utils/sellTicketUtils';
import {selectWebTLD} from '../../store/gsConfig/selectors';
import {
  connectionsIsValidSelector,
  limitationsAreValidSelector,
  restrictionsAreValidSelector
} from '../../store/seatDetails/selector';
import {
  ticketDetailsSelector,
  selectSellerFramework,
  selectIsSellerEligible,
  reasonCodeSelector,
  eventMetaDataSelector,
  fulfillmentMethodsSelector
} from '../../store/sellTicket/selectors';
import {ticketSectionSelector} from '../../store/tickets/selectors';
import {
  isGAEventSelector,
  isSeatsRequiredSelector, isSRSRequiredSelector,
  selectEventOriginCountry
} from '../higherOrderComponents/withEvents/selectors';
import {
  enterLaterSelectedSelector,
  isGASectionSelector,
  rowErrorSelector,
  ticketSelector,
  validTicket
} from '../higherOrderComponents/withTickets/utils';

export const selectTicketDetailsComplete = createSelector(
  ticketDetailsSelector,
  (ticketDetails) => ticketDetails?.sectionComplete
);

// Selector this is kinda gross. components using this should probably have this handed to them from store data.
export const isEnteringBarcodesNowSelector = createSelector(
  ticketDetailsSelector,
  (ticketDetails) => ticketDetails.deliverNow && ticketDetails.ticketTypeSelected === BARCODE
);

export const getSelectedTicketType = createSelector(
  ticketDetailsSelector,
  (state) => state?.events?.events?.[0]?.delivery?.fulfillmentMethod?.ticketType,
  (ticketDetails, fallback) => ticketDetails?.ticketTypeSelected || fallback || null
);

export const errorsInTicketDetails = createSelector(
  [
    ticketSelector,
    rowErrorSelector,
    ticketDetailsSelector,
    isGASectionSelector,
    isEnteringBarcodesNowSelector,
    isSRSRequiredSelector,
    isSeatsRequiredSelector,
    enterLaterSelectedSelector,
    selectEventOriginCountry,
    restrictionsAreValidSelector,
    limitationsAreValidSelector,
    connectionsIsValidSelector,
    ticketSectionSelector,
    isGAEventSelector
  ],
  checkErrorsInTicketDetails
);

export function checkErrorsInTicketDetails(
  {tickets = []} = {},
  rowError,
  ticketDetails,
  isGASection,
  isEnteringBarcodesNow,
  isSRSRequired,
  isSeatsRequired,
  enterLaterSelected,
  eventOriginCountry,
  restrictionsAreValid,
  limitationsAreValid,
  connectionsIsValid,
  section,
  isGAEvent
) {
  const {inhandDate = '', deliverNow, faceValue} = ticketDetails;

  const ticketError = tickets.some(
    (ticket) =>
      !validTicket(ticket, {
        isGASection,
        isSRSRequired,
        isSeatsRequired,
        isEnteringBarcodesNow,
        enterLaterSelected,
        eventOriginCountry,
        rowError
      })
  );

  return {
    noTicketsError: tickets.length <= 0,
    faceValueError: isDefined(faceValue) ? faceValue.isRequired && faceValue?.price === '0.00' : false,
    restrictionError: eventOriginCountry === 'GB' && !restrictionsAreValid,
    limitationsError: eventOriginCountry === 'GB' && !limitationsAreValid,
    connectionError: eventOriginCountry === 'GB' && !connectionsIsValid,
    deliverNowError: inhandDate === '' && !deliverNow,
    seatDetailsError: isGAEvent ? false : ticketError || !section
  };
}

export function getAutoPricingReqBody ({eventId, sectionId, row}) {
  return {
    eventId,
    sectionId,
    row
  };
}

export function getPriceRecoReqBody ({eventId, deliveryTypeIds, sectionId, row, seats}) {
  return {
    eventId,
    deliveryTypeIds,
    sectionId,
    row,
    seats
  };
}

export function getPriceSummaryReqData({eventId, section, row, currencyCode, fulfillmentType}, value) {
  return {
    eventId,
    section,
    row,
    amountPerTicket: {
      amount: value,
      currency: currencyCode
    },
    fulfillmentType
  };
}

export const selectEventCurrency = (
  (state) => state?.eventMetaData?.currencyCode
);

export const selectCountriesData = createSelector(
  selectSellerFramework,
  (sellerFramework) => sellerFramework?.countriesData
);

export const selectCountryCode = createSelector(
  selectSellerFramework,
  (sellerFramework) => sellerFramework?.countryCode
);

export const selectStatesData = createSelector(
  selectSellerFramework,
  (sellerFramework) => sellerFramework?.statesData
);

export const selectIsHWRegistered = createSelector(
  selectSellerFramework,
  (sellerFramework) => sellerFramework?.isUserHWRegistered
);

export const selectHWUserType = createSelector(
  selectSellerFramework,
  (sellerFramework) => sellerFramework?.HWUserType && sellerFramework.HWUserType[0]
);

export const selectHWFormData = createSelector(
  selectSellerFramework,
  (sellerFramework) => sellerFramework?.formData || null
);

export const selectPrimaryContact = (
  (state) => state?.user?.primaryContact
);

export const selectCountryPrimaryContact = (
  (state) => state?.user?.primaryContact?.address?.country
);

export const shouldShowDisclosureSelector = createSelector(
  eventMetaDataSelector,
  selectIsSellerEligible,
  reasonCodeSelector,
  selectHWUserType,
  selectWebTLD,
  fulfillmentMethodsSelector,
  (eventMetaData, isSellerEligible, reasonCode, hwUserType, defaultWebTLD, fulfillmentMethods) => {
    const shouldExcludeEUBiz = shouldExcludeEUBusinessSeller(hwUserType, defaultWebTLD);
    const msgObj = getDisclosureMsgType(eventMetaData, shouldExcludeEUBiz, fulfillmentMethods, isSellerEligible, reasonCode);

    return shouldShowDisclosure(msgObj, fulfillmentMethods, isSellerEligible, reasonCode);
  }
);
